import BaseApiService from './BaseApiService'

export interface AuditMe { firm: string | null; purpose: string | null; expiresAt: string | null; preview: boolean; populations: string[] }
export interface AuditControl { controlKey: string; title: string; description: string; criteria: string; frequency: string; kind: string; docPath: string | null; evidenceHint: string | null }
export interface ControlOccurrence { period: string; dueAt: string; status: string; completedAt: string | null; evidence: string | null }
export interface ControlCalendarRow { controlKey: string; title: string; criteria: string; kind: string; frequency: string; expected: number; met: number; occurrences: ControlOccurrence[] }
export interface PopulationRow { seq: number | null; at: string; type: string; actor: string | null; subject: string | null; teamId: string | null; details: string | null; link: string | null }
export interface PolicySummary { name: string; title: string; effective: string | null; sha256: string }
export interface AuditorRequest {
  auditorRequestId: string; auditorGrantId: string; title: string; details: string | null; status: string; createdAt: string
  answeredAt: string | null; responseNotes: string | null; responseFileName: string | null; firm: string | null
}
export interface AuditorGrant {
  auditorGrantId: string; userId: string; firm: string; purpose: string | null; grantedAt: string; expiresAt: string
  revokedAt: string | null; email: string | null
}

const w = (since: string, until: string) => ({ since, until })

/** Read-only SOC 2 Type II auditor portal (api/v1/audit); every call is logged server-side. */
class AuditorApiService extends BaseApiService {
  me() { return this.post<AuditMe>('/v1/audit/me') }
  controls() { return this.post<AuditControl[]>('/v1/audit/controls') }
  calendar(since: string, until: string) { return this.post<ControlCalendarRow[]>('/v1/audit/calendar', w(since, until)) }
  population(category: string, since: string, until: string) {
    return this.post<PopulationRow[]>('/v1/audit/population', { category, ...w(since, until), format: 'json' })
  }
  populationCsv(category: string, since: string, until: string) {
    return this.request<Blob>('POST', '/v1/audit/population', { category, ...w(since, until), format: 'csv' }, true)
  }
  sample(since: string, until: string, seqs: number[], prNumbers: number[]) {
    return this.request<Blob>('POST', '/v1/audit/sample', { ...w(since, until), seqs, prNumbers }, true)
  }
  packageZip(since: string, until: string) { return this.request<Blob>('POST', '/v1/audit/package', w(since, until), true) }
  policies() { return this.post<PolicySummary[]>('/v1/audit/policies') }
  policy(name: string) { return this.post<PolicySummary & { markdown: string }>('/v1/audit/policies/get', { name }) }
  requests() { return this.post<AuditorRequest[]>('/v1/audit/requests') }
  createRequest(title: string, details: string) { return this.post<{ requestId: string }>('/v1/audit/requests/create', { title, details }) }
  requestResponse(requestId: string) { return this.request<Blob>('POST', '/v1/audit/requests/response', { requestId }, true) }

  // Super-admin management (api/v1/admin/auditors)
  listGrants() { return this.post<AuditorGrant[]>('/v1/admin/auditors/list') }
  invite(email: string, firm: string, purpose: string, expiresAt: string) {
    return this.post<{ grantId: string; invited: boolean }>('/v1/admin/auditors/invite', { email, firm, purpose, expiresAt })
  }
  revoke(grantId: string) { return this.post<{ success: boolean }>('/v1/admin/auditors/revoke', { grantId }) }
  extend(grantId: string, expiresAt: string) { return this.post<{ success: boolean }>('/v1/admin/auditors/extend', { grantId, expiresAt }) }
  allRequests() { return this.post<AuditorRequest[]>('/v1/admin/auditors/requests') }
  answer(requestId: string, notes: string, file: File | null) {
    const form = new FormData()
    form.append('requestId', requestId)
    form.append('notes', notes)
    if (file) form.append('file', file)
    return this.postForm<{ success: boolean }>('/v1/admin/auditors/requests/answer', form)
  }
}

export const auditorApiService = new AuditorApiService()

export function saveBlob(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = name
  a.click()
  URL.revokeObjectURL(url)
}
